import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import LiveExchangeRate from "../../../components/LiveExchangeRate";

const INFO_ITEMS = [
  {
    tag: "Entry Requirements",
    icon: "Stamp",
    accent: "bg-blue-100 text-blue-700",
    gradient: "from-blue-500 to-blue-600",
    title: "Nepal Visa Information",
    description:
      "Getting your Nepal visa is straightforward. Most nationalities can get a visa on arrival at Kathmandu airport or land borders — no embassy visit needed.",
    details: [
      "Visa on arrival for most nationalities",
      "15 days: $30 | 30 days: $50 | 90 days: $125",
      "Multiple entry allowed for tourists",
      "Easy online pre-application available",
    ],
    cta: "View Visa Details",
    path: "/nepal-visa-information",
    image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop",
  },
  {
    tag: "Plan Your Trip",
    icon: "Backpack",
    accent: "bg-emerald-100 text-emerald-700",
    gradient: "from-emerald-500 to-emerald-600",
    title: "Travel Information",
    description:
      "From health tips to packing lists — everything you need for a smooth journey through Nepal. Stay connected, stay safe, and travel smart.",
    details: [
      "No mandatory vaccinations — hepatitis & typhoid recommended",
      "ATMs widely available in cities; carry cash for treks",
      "Buy local SIM at airport (Ncell or Nepal Telecom)",
      "Respect local customs: dress modestly at temples",
    ],
    cta: "Travel Tips",
    path: "/travel-information",
    image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800&h=600&fit=crop",
  },
  {
    tag: "How to Get Here",
    icon: "Plane",
    accent: "bg-sky-100 text-sky-700",
    gradient: "from-sky-500 to-sky-600",
    title: "Getting to Nepal",
    description:
      "Nepal is well-connected by air and land. Tribhuvan International Airport in Kathmandu serves flights from major cities across Asia, Europe, and the Middle East.",
    details: [
      "Direct flights from Delhi, Dubai, Doha, Bangkok, Singapore, Istanbul",
      "Land borders from India at Sunauli, Raxaul, and Kakarbhitta",
      "Domestic flights to Lukla, Pokhara, Chitwan, and more",
      "Airport is 5km from Kathmandu city center",
    ],
    cta: "Travel Routes",
    path: "/getting-to-nepal",
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&h=600&fit=crop",
  },
  {
    tag: "Best Time to Visit",
    icon: "Sun",
    accent: "bg-amber-100 text-amber-700",
    gradient: "from-amber-500 to-amber-600",
    title: "Weather & Climate",
    description:
      "Nepal's climate varies dramatically — from tropical Terai to arctic high Himalayas. Spring and autumn offer the clearest skies and best trekking conditions.",
    details: [
      "Spring (Mar-May): Rhododendron blooms, warm temps, clear skies",
      "Autumn (Sep-Nov): Peak season, crystal clear mountain views",
      "Monsoon (Jun-Aug): Lush green, great for rain-shadow treks",
      "Winter (Dec-Feb): Cold but clear, fewer crowds, lower prices",
    ],
    cta: "Season Guide",
    path: "/weather-climate",
    image: "https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=800&h=600&fit=crop",
  },
  {
    tag: "Markets & Culture",
    icon: "ShoppingBag",
    accent: "bg-purple-100 text-purple-700",
    gradient: "from-purple-500 to-purple-600",
    title: "Shopping & Entertainment",
    description:
      "From Thamel's bustling markets to Bhaktapur's pottery square — Nepal is a shopper's paradise. Discover pashminas, singing bowls, and handcrafted treasures.",
    details: [
      "Thamel: Trekking gear, pashminas, handicrafts, souvenirs",
      "Asan Bazaar: Spices, grains, authentic local market experience",
      "Patan: Tibetan carpets, metalwork, and ancient artisans",
      "Nightlife: Live music bars in Thamel and Lakeside Pokhara",
    ],
    cta: "Explore Shopping",
    path: "/shopping-entertainment",
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
  },
  {
    tag: "Quick Answers",
    icon: "HelpCircle",
    accent: "bg-rose-100 text-rose-700",
    gradient: "from-rose-500 to-rose-600",
    title: "Nepal FAQ",
    description:
      "Still have questions? Our comprehensive FAQ covers everything from safety and permits to currency, language, and connectivity for your Nepal trip.",
    details: [
      "Is Nepal safe? Yes — very safe for solo travelers",
      "Do I need a guide? Recommended but not required on most treks",
      "What permits? TIMS card + national park entry (NPR 3,000-5,000)",
    ],
    cta: "View All FAQs",
    path: "/nepal-faq",
    image: "https://images.unsplash.com/photo-1578898887932-dce23a595ad4?w=800&h=600&fit=crop",
  },
];

const NepalInfoSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary/10 text-primary text-sm font-semibold rounded-full mb-4">
            <Icon name="FileText" size={14} />
            Plan Your Journey
          </span>
          <h2 className="section-heading text-foreground mb-5">
            Nepal <span style={{ color: "#c4873a" }}>Travel Information</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to know before traveling to Nepal — curated by our team of local experts.
          </p>
        </div>

        {/* Info items */}
        <div className="space-y-20 lg:space-y-28">
          {INFO_ITEMS.map((item, i) => (
            <div
              key={i}
              className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              {/* Image */}
              <div className="relative group">
                <div className={`absolute -inset-4 bg-gradient-to-br ${item.gradient} opacity-10 rounded-3xl blur-xl`} />
                <div className="relative overflow-hidden rounded-2xl shadow-xl">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-80 lg:h-[440px] object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-5 left-5 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2.5 shadow-lg">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center`}>
                        <Icon name={item.icon} size={16} color="white" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 leading-none">{item.tag}</p>
                        <p className="text-sm font-bold text-gray-900 mt-0.5">{item.title}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${item.accent}`}>
                  <Icon name={item.icon} size={12} />
                  {item.tag}
                </span>

                <h3 className="font-headline text-2xl sm:text-3xl text-gray-900 mb-4" style={{ fontWeight: 300 }}>
                  {item.title}
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  {item.description}
                </p>

                {/* Details */}
                <ul className="space-y-2.5 mb-8">
                  {item.details.map((d, di) => (
                    <li key={di} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="Check" size={11} className="text-primary" />
                      </span>
                      {d === "" ? <LiveExchangeRate compact /> : <span className="text-gray-700 text-sm">{d}</span>}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(item.path)}
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-accent text-accent font-semibold rounded-xl hover:bg-accent hover:text-white transition-all duration-200 text-sm"
                >
                  {item.cta}
                  <Icon name="ArrowRight" size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default NepalInfoSection;
